import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Typography, TextField, Button, IconButton, Grid, MenuItem, Switch, FormControlLabel,Card } from "@mui/material";
import { toast } from "react-hot-toast";
import Autocomplete from "@mui/material/Autocomplete";
import CloseIcon from "@mui/icons-material/Close";
import { Formik, Form } from "formik";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllSeasonsAction,
  getAllZonesAction,
  getAllRegionsAction,
  getAllAreasAction,
  getAllDistrictsAction,
  getAllMandalsAction,
  createPreSeasonActivityAction,
  clearCreatePreSeasonActivityAction,
  updatePreSeasonActivityAction,
  clearUpdatePreSeasonActivityAction,
  getPreSeasonActivityByIdAction,
} from "../../store/actions";

// No longer accepts editData prop; uses Redux state instead
export default function PreSeasonInformation() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const id = location?.state?.activityId;
  const isEditMode = Boolean(id);

  // Redux selectors

  const { successData: seasons } = useSelector((state) => state.getAllSeasonsReducer);
  const seasonList = seasons?.data?.seasons || [];

  const { successData: zones, loading: zonesLoading } = useSelector((state) => state.getAllZonesReducer);
  const zoneList = zones?.data?.zones || [];

  const { successData: regions, loading: regionsLoading } = useSelector((state) => state.getAllRegionsReducer);
  const regionList = regions?.data?.regions || [];

  const { successData: areas, loading: areasLoading } = useSelector((state) => state.getAllAreasReducer);
  const areaList = areas?.data?.areas || [];

  const { successData: districts, loading: districtsLoading } = useSelector((state) => state.getAllDistrictsReducer);
  const districtList = districts?.data?.districts || [];

  const { successData: mandals, loading: mandalsLoading } = useSelector((state) => state.getAllMandalsReducer);
  const mandalList = mandals?.data?.mandals || [];

  const { successData: createPreSeasonSuccess ,error:createPreSeasonError,errorInfo:createPreSeasonErrorInfo} = useSelector((state) => state.createPreSeasonActivityReducer);
  const { successData: updatePreSeasonSuccess ,error:updatePreSeasonError} = useSelector((state) => state.updatePreSeasonActivityReducer);
  const { successData: preSeasonActivityById } = useSelector((state) => state.getPreSeasonActivityByIdReducer);

  useEffect(() => {
    dispatch(getAllSeasonsAction());
    dispatch(getAllZonesAction());
    dispatch(getAllRegionsAction());
    dispatch(getAllAreasAction());
    dispatch(getAllDistrictsAction());
    dispatch(getAllMandalsAction());

  }, [dispatch]);
  
  useEffect(() => {
    if (id) {
      dispatch(getPreSeasonActivityByIdAction({
        endPoint : `target/pre-season/${id}`,
      }));
    }
  }, [dispatch, id]);

  // Extract edit data from Redux state
  const editData = preSeasonActivityById?.data || null;
  
  // Toast and navigation for create
  useEffect(() => {
    if (createPreSeasonSuccess) {
      toast.success("Pre season created successfully");
      dispatch(clearCreatePreSeasonActivityAction());
      navigate(-1);
    } else if (createPreSeasonError) {
      const errMsg = createPreSeasonErrorInfo?.message || createPreSeasonErrorInfo || "Failed to create pre activity";
      toast.error(errMsg);
      dispatch(clearCreatePreSeasonActivityAction());
    }
  }, [createPreSeasonSuccess, createPreSeasonError, createPreSeasonErrorInfo, dispatch, navigate]);

  // Toast and navigation for update
  useEffect(() => {
    if (updatePreSeasonSuccess) {
      toast.success("Pre activity updated successfully");
      dispatch(clearUpdatePreSeasonActivityAction());
      navigate(-1);
    } else if (updatePreSeasonError) {
      toast.error("Failed to update pre activity");
      dispatch(clearUpdatePreSeasonActivityAction());
    }
  }, [updatePreSeasonSuccess, updatePreSeasonError, dispatch, navigate]);

  // Visibility mapping for location fields
  const fieldsByType = {
    Zone: ["zone"],
    Region: ["zone", "region"],
    Area: ["zone", "region", "area"],
    district: ["zone", "region", "area", "district"],
    mandal: ["zone", "region", "area", "district", "mandal"],
  };

  
  // Initial values for create or update
  const initialValues = editData
    ? (() => {
        // Helper function to populate hierarchy based on target_area_type and target_area_id
        const populateHierarchy = () => {
          const hierarchy = {
            zoneId: "",
            regionId: "",
            areaId: "",
            districtId: "",
            mandalId: "",
          };

          const targetType = editData.target_area_type;
          const targetId = editData.target_area_id;

          if (targetType === "Zone") {
            hierarchy.zoneId = targetId;
          } else if (targetType === "Region") {
            const region = regionList.find(r => r.regionId === targetId);
            if (region) {
              hierarchy.zoneId = region.zoneId;
              hierarchy.regionId = targetId;
            }
          } else if (targetType === "Area") {
            const area = areaList.find(a => a.areaId === targetId);
            if (area) {
              const region = regionList.find(r => r.regionId === area.regionId);
              if (region) {
                hierarchy.zoneId = region.zoneId;
                hierarchy.regionId = area.regionId;
              }
              hierarchy.areaId = targetId;
            }
          } else if (targetType === "district") {
            const district = districtList.find(d => d.districtId === targetId);
            if (district) {
              const area = areaList.find(a => a.areaId === district.areaId);
              if (area) {
                const region = regionList.find(r => r.regionId === area.regionId);
                if (region) {
                  hierarchy.zoneId = region.zoneId;
                  hierarchy.regionId = area.regionId;
                }
                hierarchy.areaId = area.areaId;
              }
              hierarchy.districtId = targetId;
            }
          } else if (targetType === "mandal") {
            const mandal = mandalList.find(m => m.mandalId === targetId);
            if (mandal) {
              const district = districtList.find(d => d.districtId === mandal.districtId);
              if (district) {
                const area = areaList.find(a => a.areaId === district.areaId);
                if (area) {
                  const region = regionList.find(r => r.regionId === area.regionId);
                  if (region) {
                    hierarchy.zoneId = region.zoneId;
                    hierarchy.regionId = area.regionId;
                  }
                  hierarchy.areaId = area.areaId;
                }
                hierarchy.districtId = district.districtId;
              }
              hierarchy.mandalId = targetId;
            }
          }

          return hierarchy;
        };

        const hierarchy = populateHierarchy();

        return {
          seasonId: editData.season_id || "",
          targetLocationType: editData.target_area_type || "",
          vlmCount: editData.vlm_count ?? "",
          mvlmCount: editData.mvlm_count ?? "",
          farmers_attended: editData.farmers_attended ?? "",
          ...hierarchy,
          is_active: typeof editData.is_active === "boolean" ? editData.is_active : true,
        };
      })()
    : {
        seasonId: "",
        targetLocationType: "",
        zoneId: "",
        regionId: "",
        areaId: "",
        districtId: "",
        mandalId: "",
        vlmCount: "",
        mvlmCount: "",
        farmers_attended: "",
        is_active: true,
      };

  // Build payload for create/update
  const buildPayload = (values) => {
    let target_area_type = values.targetLocationType.toLowerCase();
    let target_area_id = null;
    
    if (target_area_type === "zone") {
      target_area_id = Number(values.zoneId);
    } else if (target_area_type === "region") {
      target_area_id = Number(values.regionId);
    } else if (target_area_type === "area") {
      target_area_id = Number(values.areaId);
    } else if (target_area_type === "district") {
      target_area_id = Number(values.districtId);
    } else if (target_area_type === "mandal") {
      target_area_id = Number(values.mandalId);
    }
    
    return {
      season_id: Number(values.seasonId),
      target_area_type,
      target_area_id,
      vlm_count: Number(values.vlmCount),
      mvlm_count: Number(values.mvlmCount),
      farmers_attended: Number(values.farmers_attended),
      is_active: values.is_active,
    };
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
       mt:{sm:7,md:0}
       

      }}
    >
      <Card
        sx={{
          background: "#FFFFFF",
          borderRadius: 3,
          p:1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Typography variant="h4" fontWeight={600}>
            Pre season Information
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton onClick={() => navigate(-1)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <Formik
          initialValues={initialValues}
          enableReinitialize
          onSubmit={(values, { setSubmitting }) => {
            const payload = buildPayload(values);
            if (isEditMode && id) {
              // Update mode
              dispatch(updatePreSeasonActivityAction({ ...payload, endPoint: `target/pre-season/${id}` }));
            } else {
              // Create mode
              dispatch(createPreSeasonActivityAction(payload));
            }
            setSubmitting(false);
          }}
        >
          {(formik) => {
            const { values, handleChange, setFieldValue, isSubmitting } = formik;
            const locationFields = fieldsByType[values.targetLocationType] || [];
            
            // Debug: Log formik values
            console.log("Formik values:", values);
            
            return (
              <Form>
                <Box display="flex" justifyContent="flex-end" mb={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        name="is_active"
                        checked={values.is_active}
                        onChange={(e) => setFieldValue("is_active", e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Active"
                    labelPlacement="start"
                  />
                </Box>
                <Grid container spacing={3}>
                  {/* Season */}
                  <Grid item sx={{ flexGrow: 1 }} size={{ xs: 12, sm: 6, }}>
                    <Autocomplete
                      options={seasonList.map(s => ({ id: s.seasonId, label: s.name + (s.year ? ` (${s.year})` : '') }))}
                      value={seasonList.map(s => ({ id: s.seasonId, label: s.name + (s.year ? ` (${s.year})` : '') })).find(s => s.id === values.seasonId) || null}
                      onChange={(e, val) => setFieldValue('seasonId', val ? val.id : '')}
                      getOptionLabel={option => option?.label || ''}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      clearOnEscape
                      renderInput={params => (
                        <TextField
                          {...params}
                          fullWidth
                          size="small"
                          label="Season"
                          name="seasonId"
                          placeholder="Search season..."
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              backgroundColor: '#FAFAFA',
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                    {/* Target Area Type */}
                  <Grid item sx={{ flexGrow: 1 }} size={{ xs: 12, sm: 6, }}>
                    <TextField
                      size="small"
                      select
                      fullWidth
                      label="Target Area Type"
                      name="targetLocationType"
                      value={values.targetLocationType}
                      onChange={(e) => {
                        handleChange(e);
                        setFieldValue("zoneId", "");
                        setFieldValue("regionId", "");
                        setFieldValue("areaId", "");
                        setFieldValue("districtId", "");
                        setFieldValue("mandalId", "");
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: '#FAFAFA',
                        },
                      }}
                    >
                      <MenuItem value="">Select Type</MenuItem>
                      <MenuItem value="Zone">Zone</MenuItem>
                      <MenuItem value="Region">Region</MenuItem>
                      <MenuItem value="Area">Area</MenuItem>
                      <MenuItem value="district">District</MenuItem>
                      <MenuItem value="mandal">Mandal</MenuItem>
                    </TextField>
                  </Grid>
                  </Grid>
                  
                  <Grid container spacing={3} mt={2}>
                  {/* Zone Autocomplete */}
                  {locationFields.includes("zone") && (
                    <Grid item sx={{ flexGrow: 1 }} size={{ xs: 12, sm: 3, md: 3 }}>
                      <Autocomplete
                        options={zoneList.map(z => ({ id: z.zoneId, label: z.zoneName }))}
                        loading={zonesLoading}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: '#FAFAFA',
                          },
                        }}
                        value={zoneList.map(z => ({ id: z.zoneId, label: z.zoneName })).find(z => z.id === values.zoneId) || null}
                        onChange={(e, val) => {
                          setFieldValue("zoneId", val ? val.id : "");
                          setFieldValue("regionId", "");
                          setFieldValue("areaId", "");
                          setFieldValue("districtId", "");
                          setFieldValue("mandalId", "");
                        }}
                        getOptionLabel={option => option?.label || ""}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        clearOnEscape
                        renderInput={params => (
                          <TextField
                            {...params}
                            fullWidth
                            size="small"
                            label="Zone"
                            name="zoneId"
                            onBlur={() => formik.setFieldTouched("zoneId", true)}
                            error={formik.touched.zoneId && Boolean(formik.errors.zoneId)}
                            helperText={formik.touched.zoneId && formik.errors.zoneId}
                          />
                        )}
                      />
                    </Grid>
                  )}
                  {/* Region Autocomplete */}
                  {locationFields.includes("region") && (
                    <Grid item sx={{ flexGrow: 1 }} size={{ xs: 12, sm: 3, md: 3 }}>
                      <Autocomplete
                        options={regionList.filter(r => r.zoneId === values.zoneId).map(r => ({ id: r.regionId, label: r.regionName }))}
                        loading={regionsLoading}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: '#FAFAFA',
                          },
                        }}
                        value={regionList.filter(r => r.zoneId === values.zoneId).map(r => ({ id: r.regionId, label: r.regionName })).find(r => r.id === values.regionId) || null}
                        onChange={(e, val) => {
                          setFieldValue("regionId", val ? val.id : "");
                          setFieldValue("areaId", "");
                          setFieldValue("districtId", "");
                          setFieldValue("mandalId", "");
                        }}
                        getOptionLabel={option => option?.label || ""}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        clearOnEscape
                        renderInput={params => (
                          <TextField
                            {...params}
                            fullWidth
                            size="small"
                            label="Region"
                            name="regionId"
                            onBlur={() => formik.setFieldTouched("regionId", true)}
                            error={formik.touched.regionId && Boolean(formik.errors.regionId)}
                            helperText={formik.touched.regionId && formik.errors.regionId}
                          />
                        )}
                      />
                    </Grid>
                  )}
                  {/* Area Autocomplete */}
                  {locationFields.includes("area") && (
                    <Grid item sx={{ flexGrow: 1 }} size={{ xs: 12, sm: 3, md: 3 }}>
                      <Autocomplete
                        options={areaList.filter(a => a.regionId === values.regionId).map(a => ({ id: a.areaId, label: a.areaName }))}
                        loading={areasLoading}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: '#FAFAFA',
                          },
                        }}
                        value={areaList.filter(a => a.regionId === values.regionId).map(a => ({ id: a.areaId, label: a.areaName })).find(a => a.id === values.areaId) || null}
                        onChange={(e, val) => {
                          setFieldValue("areaId", val ? val.id : "");
                          setFieldValue("districtId", "");
                          setFieldValue("mandalId", "");
                        }}
                        getOptionLabel={option => option?.label || ""}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        clearOnEscape
                        renderInput={params => (
                          <TextField
                            {...params}
                            fullWidth
                            size="small"
                            label="Area"
                            name="areaId"
                            onBlur={() => formik.setFieldTouched("areaId", true)}
                            error={formik.touched.areaId && Boolean(formik.errors.areaId)}
                            helperText={formik.touched.areaId && formik.errors.areaId}
                          />
                        )}
                      />
                    </Grid>
                  )}
                  {/* District Autocomplete */}
                  {locationFields.includes("district") && (
                    <Grid item sx={{ flexGrow: 1 }} size={{ xs: 12, sm: 3, md: 3 }}>
                      <Autocomplete
                        options={districtList.filter(d => d.areaId === values.areaId).map(d => ({ id: d.districtId, label: d.name }))}
                        loading={districtsLoading}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: '#FAFAFA',
                          },
                        }}
                        value={districtList.filter(d => d.areaId === values.areaId).map(d => ({ id: d.districtId, label: d.name })).find(d => d.id === values.districtId) || null}
                        onChange={(e, val) => {
                          setFieldValue("districtId", val ? val.id : "");
                          setFieldValue("mandalId", "");
                        }}
                        getOptionLabel={option => option?.label || ""}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        clearOnEscape
                        renderInput={params => (
                          <TextField
                            {...params}
                            fullWidth
                            size="small"
                            label="District"
                            name="districtId"
                            onBlur={() => formik.setFieldTouched("districtId", true)}
                            error={formik.touched.districtId && Boolean(formik.errors.districtId)}
                            helperText={formik.touched.districtId && formik.errors.districtId}
                          />
                        )}
                      />
                    </Grid>
                  )}
                  {/* Mandal Autocomplete */}
                  {locationFields.includes("mandal") && (
                    <Grid item sx={{ flexGrow: 1 }} size={{ xs: 12, sm: 3, md: 3 }}>
                      <Autocomplete
                        options={mandalList.filter(m => m.districtId === values.districtId).map(m => ({ id: m.mandalId, label: m.name }))}
                        loading={mandalsLoading}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: '#FAFAFA',
                          },
                        }}
                        value={mandalList.filter(m => m.districtId === values.districtId).map(m => ({ id: m.mandalId, label: m.name })).find(m => m.id === values.mandalId) || null}
                        onChange={(e, val) => {
                          setFieldValue("mandalId", val ? val.id : "");
                        }}
                        getOptionLabel={option => option?.label || ""}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        clearOnEscape
                        renderInput={params => (
                          <TextField
                            {...params}
                            fullWidth
                            size="small"
                            label="Mandal"
                            name="mandalId"
                            onBlur={() => formik.setFieldTouched("mandalId", true)}
                            error={formik.touched.mandalId && Boolean(formik.errors.mandalId)}
                            helperText={formik.touched.mandalId && formik.errors.mandalId}
                          />
                        )}
                      />
                    </Grid>
                  )}
                  </Grid>

                 <Grid container spacing={3} mt={2}>
                  {/* VLM Count */}
                  <Grid item sx={{ flexGrow: 1 }} size={{ xs: 12, sm: 6,  }}>
                    <TextField
                      size="small"
                      fullWidth
                      label="VLM Count"
                      name="vlmCount"
                      placeholder="Enter VLM count"
                      value={values.vlmCount}
                      onChange={handleChange}
                      slotProps={{
                        input: {
                          inputMode: 'numeric',
                          pattern: '[0-9]*',
                          style: { MozAppearance: 'textfield' }
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: '#FAFAFA',
                        },
                        '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0,
                        },
                        '& input[type=number]': {
                          MozAppearance: 'textfield',
                        },
                      }}
                    />
                  </Grid>
                  {/* MVLM Count */}
                  <Grid item sx={{ flexGrow: 1 }} size={{ xs: 12, sm: 6, }}>
                    <TextField
                      size="small"
                      fullWidth
                      label="MVLM Count"
                      name="mvlmCount"
                      placeholder="Enter MVLM count"
                      value={values.mvlmCount}
                      onChange={handleChange}
                      slotProps={{
                        input: {
                          inputMode: 'numeric',
                          pattern: '[0-9]*',
                          style: { MozAppearance: 'textfield' }
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: '#FAFAFA',
                        },
                        '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0,
                        },
                        '& input[type=number]': {
                          MozAppearance: 'textfield',
                        },
                      }}
                    />
                  </Grid>
                  </Grid>

                  {/* Farmers Attended */}
                  {/* <Grid item sx={{ flexGrow: 1 }} size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField
                      size="small"
                      type="number"
                      fullWidth
                      label="Farmers Attended"
                      name="farmers_attended"
                      placeholder="Enter Farmers Attended"
                      value={values.farmers_attended}
                      onChange={handleChange}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: '#FAFAFA',
                        },
                      }}
                    />
                  </Grid> */}
                {/* </Grid> */}
                {/* Actions */}
                <Box
                  mt={5}
                >
                  <Grid container justifyContent={{ xs: 'stretch', sm: 'flex-end' }}>
                    <Grid item xs={12} sm={8} md={6} lg={4}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Button
                            variant="outlined"
                            sx={{
                              width: '100%',
                              borderRadius: 3,
                              textTransform: "none",
                            }}
                            onClick={() => navigate(-1)}
                          >
                            Cancel
                          </Button>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Button
                            type="submit"
                            variant="contained"
                            sx={{
                              width: '100%',
                              borderRadius: 3,
                              background: "#4F46E5",
                              textTransform: "none",
                            }}
                            disabled={isSubmitting}
                          >
                            {isEditMode ? "Update" : "Submit"}
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Box>
              </Form>
            );
          }}
        </Formik>
      </Card>
    </Box>
  );
}

